import {
  css,
  Button,
  tokens,
  formatTime,
  timestampToDateString,
  timestampToTimeString,
} from "@balabalabom/ui";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { parseDate } from "../lib/date";
import { Link } from "@balabalabom/ui";
import { eventService } from "../lib/getClient";
import { storiesService } from "../lib/storiesClient";
import { toEmbeddableUrl, isYoutubeUrl } from "../lib/embedUrl";
import { useFullscreenIframe } from "../hooks/useFullscreenIframe";
import { EventKindBadge } from "../components/EventKindBadge";
import { resolveEventKind, type EventKind } from "../lib/eventKind";
import { Event as GrpcEvent } from "../generated/events/v1/event_pb";
import {
  Story,
  MonthlyTheme,
  ResourceType,
} from "../generated/stories/v1/story_pb";

interface Event {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  address: string;
  image: string;
  details: string;
  requirements: string;
  targetAge: string;
  maxParticipants: number;
  registrationFee: number;
  facebookLink: string;
  registrationOpen: boolean;
  type?: EventKind;
}

export function Home() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [monthlySections, setMonthlySections] = useState<
    Array<{
      theme: MonthlyTheme.AsObject;
      stories: Story.AsObject[];
    }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Do not display stories tagged "hide" on the public site
  const isStoryHidden = (story: Story.AsObject): boolean =>
    story.tagsList?.some((t) => t.toLowerCase() === "hide") ?? false;

  const { open: openEmbedModal, Modal: EmbedModal } =
    useFullscreenIframe("Preview");

  // Function to calculate event status based on date
  const getEventStatus = (eventDate: string): "current" | "future" | "past" => {
    const today = new Date();
    const eventDateObj = parseDate(eventDate);

    today.setHours(0, 0, 0, 0);
    eventDateObj.setHours(0, 0, 0, 0);

    const timeDiff = eventDateObj.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0) {
      return "past";
    } else if (daysDiff <= 7) {
      return "current";
    } else {
      return "future";
    }
  };

  // Helper function to convert gRPC Event.AsObject to component Event interface
  const mapGrpcEventToEvent = (grpcEvent: GrpcEvent.AsObject): Event => {
    // Prefer datetime (single source of truth); fallback to date/time for legacy rows
    const eventDate = grpcEvent.datetime
      ? timestampToDateString(grpcEvent.datetime)
      : grpcEvent.date || "";
    const eventTime = grpcEvent.datetime
      ? timestampToTimeString(grpcEvent.datetime)
      : grpcEvent.time || "";
    return {
      id: grpcEvent.id,
      title: grpcEvent.title,
      date: eventDate,
      time: eventTime,
      location: grpcEvent.location,
      address: grpcEvent.address,
      image: grpcEvent.imageUrl,
      details: grpcEvent.details,
      requirements: grpcEvent.requirements,
      targetAge: grpcEvent.targetAge,
      maxParticipants: grpcEvent.maxParticipants,
      registrationFee: grpcEvent.registrationFee,
      facebookLink: grpcEvent.facebookLink,
      registrationOpen: grpcEvent.registrationOpen,
      type: resolveEventKind({
        title: grpcEvent.title,
        details: grpcEvent.details,
      }),
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch events and stories in parallel
        const [grpcEvents, homeContent] = await Promise.all([
          eventService.listEvents("all"),
          storiesService
            .getHome({
              eventsLimit: 3,
              monthsLimit: 6,
              storiesPerMonth: 8,
            })
            .catch(() => null), // Fallback if getHome fails
        ]);

        const mappedEvents = grpcEvents.map(mapGrpcEventToEvent);
        setEvents(mappedEvents);

        // If getHome worked, use it; otherwise fetch stories separately
        if (homeContent) {
          const sections: Array<{
            theme: MonthlyTheme.AsObject;
            stories: Story.AsObject[];
          }> = [];
          homeContent.monthlySectionsList?.forEach((section) => {
            const visibleStories = (section.storiesList || []).filter(
              (s) => !isStoryHidden(s)
            );
            if (section.theme && visibleStories.length > 0) {
              sections.push({
                theme: section.theme,
                stories: visibleStories,
              });
            }
          });
          setMonthlySections(sections);
        } else {
          // Fallback: fetch monthly themes and stories separately
          try {
            const themes = await storiesService.listMonthlyThemes();
            const sectionsPromises = themes.slice(0, 6).map(async (theme) => {
              try {
                const result = await storiesService.listStoriesByMonth(
                  theme.monthId,
                  {
                    page: 1,
                    pageSize: 8,
                  }
                );
                const visibleStories = (result.stories || []).filter(
                  (s) => !isStoryHidden(s)
                );
                return {
                  theme,
                  stories: visibleStories,
                };
              } catch {
                return { theme, stories: [] };
              }
            });
            const sections = await Promise.all(sectionsPromises);
            setMonthlySections(sections.filter((s) => s.stories.length > 0));
          } catch (err) {
            console.error("Error fetching stories:", err);
          }
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An error occurred";
        setError(errorMessage);
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getMapsUrl = (e: Event) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      e.address || e.location
    )}`;

  // Get the current or next coming event (single event for homepage)
  const getFeaturedEvent = (): Event | null => {
    const current = events.find((e) => getEventStatus(e.date) === "current");
    if (current) return current;

    const future = events
      .filter((e) => getEventStatus(e.date) === "future")
      .sort(
        (a, b) => parseDate(a.date).getTime() - parseDate(b.date).getTime()
      )[0];

    return future || null;
  };

  const featuredEvent = getFeaturedEvent();

  // Get month-specific color for tags
  const getMonthColor = (monthNumber: number): string => {
    const colors = [
      "#FF6B9D", // Month 1 - Pink
      "#FF8C42", // Month 2 - Orange
      "#FFB347", // Month 3 - Orange-red/Coral
      "#4ECDC4", // Month 4 - Teal
      "#95E1D3", // Month 5 - Mint
      "#E74C3C", // Month 6 - Red
      "#9B59B6", // Month 7 - Purple
      "#3498DB", // Month 8 - Blue
      "#1ABC9C", // Month 9 - Turquoise
      "#F39C12", // Month 10 - Orange
      "#E67E22", // Month 11 - Dark Orange
      "#16A085", // Month 12 - Green
    ];
    // Use modulo to cycle through colors if month number exceeds 12
    return colors[(monthNumber - 1) % colors.length];
  };

  // Get lighter pastel version of month color for backgrounds
  const getMonthBackgroundColor = (monthNumber: number): string => {
    const baseColor = getMonthColor(monthNumber);
    // Convert hex to RGB and lighten by mixing with white (85% white, 15% color)
    const hex = baseColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    // Mix with white (85% white = 255 * 0.85 = 216.75, round to 217)
    const lightR = Math.round(r * 0.15 + 217);
    const lightG = Math.round(g * 0.15 + 217);
    const lightB = Math.round(b * 0.15 + 217);
    return `rgb(${lightR}, ${lightG}, ${lightB})`;
  };

  if (loading) {
    return (
      <main
        className="main-mobile"
        style={css({
          maxWidth: "80rem",
          margin: "0 auto",
          textAlign: "center",
        })}
      >
        <div
          style={css({
            fontSize: "1.25rem",
            color: "#c85a3a",
            marginBottom: "1rem",
          })}
        >
          Loading...
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main
        className="main-mobile"
        style={css({
          maxWidth: "80rem",
          margin: "0 auto",
          textAlign: "center",
        })}
      >
        <div
          style={css({
            fontSize: "1.25rem",
            color: "#ef4444",
            marginBottom: "1rem",
          })}
        >
          Error loading content: {error}
        </div>
        <Button variant="primary" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </main>
    );
  }

  return (
    <>
      <main
        className="main-mobile"
        style={css({ maxWidth: "80rem", margin: "0 auto" })}
      >
        {/* Main Title Section */}
        <section
          className="section-mobile"
          style={css({ textAlign: "center", marginBottom: "3rem" })}
        >
          <h1
            style={css({
              fontSize: "2rem",
              fontWeight: "700",
              color: tokens.colors.brand.deep,
              marginBottom: "0.5rem",
              "@media (min-width: 768px)": {
                fontSize: "3rem",
              },
            })}
          >
            Balabalabom Bilingual Story Library
          </h1>
          <p
            style={css({
              fontSize: "1rem",
              color: tokens.colors.neutral[600],
              "@media (min-width: 768px)": {
                fontSize: "1.25rem",
              },
            })}
          >
            English + Mandarin stories, songs, shorts, audio, and ebooks.
          </p>
        </section>

        {/* Upcoming Events Section */}
        <section className="section-mobile">
          {/* Main Card Container */}
          <div
            style={css({
              backgroundColor: "#F8EFEA",
              borderRadius: "20px",
              padding: "2rem",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              marginBottom: "2rem",
              "@media (min-width: 768px)": {
                padding: "2.5rem",
                borderRadius: "24px",
              },
            })}
          >
            {/* Header Section */}
            <div
              style={css({
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: featuredEvent ? "1.5rem" : "0",
                gap: "1rem",
              })}
            >
              <div
                style={css({
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "1rem",
                  flex: 1,
                })}
              >
                {/* Storytime Logo */}
                <div
                  style={css({
                    width: "60px",
                    height: "60px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    overflow: "hidden",
                    "@media (min-width: 768px)": {
                      width: "80px",
                      height: "80px",
                    },
                  })}
                >
                  <img
                    src="/storytime-logo.png"
                    alt="Story Time Fun"
                    style={css({
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    })}
                  />
                </div>
                <div style={css({ flex: 1 })}>
                  <h2
                    style={css({
                      fontSize: "1.5rem",
                      fontWeight: "700",
                      color: "#4A2E3D",
                      marginBottom: "0.25rem",
                      lineHeight: "1.2",
                      "@media (min-width: 768px)": {
                        fontSize: "2rem",
                      },
                    })}
                  >
                    Upcoming Events
                  </h2>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate("/events")}
                title="View all upcoming events"
                style={css({
                  backgroundColor: "#FDFDFB",
                  border: "1px solid #D5C2BE",
                  borderRadius: "9999px",
                  color: "#4A2E3D",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  padding: "0.5rem 1rem",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#4A2E3D",
                    backgroundColor: "#F8EFEA",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                  "@media (min-width: 768px)": {
                    fontSize: "0.95rem",
                    padding: "0.625rem 1.25rem",
                  },
                })}
              >
                View All Events &gt;
              </button>
            </div>

            {/* Event Content */}
            {featuredEvent ? (
              <div
                style={css({
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  padding: "1.5rem",
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                  marginTop: "1rem",
                  "@media (min-width: 768px)": {
                    padding: "2rem",
                    borderRadius: "20px",
                  },
                })}
              >
                <div
                  style={css({
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "1.25rem",
                    "@media (max-width: 640px)": {
                      flexDirection: "column",
                    },
                  })}
                >
                  {/* Calendar Icon */}
                  <div
                    style={css({
                      width: "70px",
                      height: "70px",
                      backgroundColor: "#F08080",
                      borderRadius: "12px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 3px 6px rgba(0, 0, 0, 0.15)",
                      flexShrink: 0,
                      position: "relative",
                      "@media (min-width: 768px)": {
                        width: "90px",
                        height: "90px",
                        borderRadius: "16px",
                      },
                    })}
                  >
                    <div
                      style={css({
                        width: "85%",
                        height: "85%",
                        backgroundColor: "#FFB6C1",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "@media (min-width: 768px)": {
                          borderRadius: "10px",
                        },
                      })}
                    >
                      <span
                        style={css({
                          fontSize: "1.75rem",
                          fontWeight: "700",
                          color: "#4A2E3D",
                          "@media (min-width: 768px)": {
                            fontSize: "2.25rem",
                          },
                        })}
                      >
                        {new Date(parseDate(featuredEvent.date)).getDate()}
                      </span>
                    </div>
                  </div>

                  {/* Event Details */}
                  <div style={css({ flex: 1, minWidth: 0 })}>
                    {/* Title and Time */}
                    <div
                      style={css({
                        display: "flex",
                        alignItems: "baseline",
                        gap: "0.75rem",
                        marginBottom: "0.75rem",
                        flexWrap: "wrap",
                      })}
                    >
                      <h3
                        style={css({
                          fontSize: "1.125rem",
                          fontWeight: "700",
                          color: "#4A2E3D",
                          margin: 0,
                          "@media (min-width: 768px)": {
                            fontSize: "1.375rem",
                          },
                        })}
                      >
                        {featuredEvent.title}
                      </h3>
                      <EventKindBadge event={featuredEvent} />
                      <span
                        style={css({
                          fontSize: "0.95rem",
                          color: "#4A2E3D",
                          fontWeight: "400",
                          "@media (min-width: 768px)": {
                            fontSize: "1.05rem",
                          },
                        })}
                      >
                        {formatTime(featuredEvent.time)}
                      </span>
                    </div>

                    {/* Location */}
                    <div
                      style={css({
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        marginBottom: "0.5rem",
                      })}
                    >
                      <span
                        style={css({
                          color: "#FF6347",
                          fontSize: "1rem",
                          fontWeight: "bold",
                        })}
                      >
                        ✓
                      </span>
                      <Link
                        href={getMapsUrl(featuredEvent)}
                        external
                        style={css({
                          color: "#4A2E3D",
                          fontSize: "0.9rem",
                          textDecoration: "none",
                          opacity: 0.85,
                          "&:hover": {
                            textDecoration: "underline",
                            opacity: 1,
                          },
                          "@media (min-width: 768px)": {
                            fontSize: "0.95rem",
                          },
                        })}
                      >
                        {featuredEvent.location}
                      </Link>
                    </div>

                    {/* Description */}
                    <div
                      style={css({
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.5rem",
                        marginBottom: "1.25rem",
                      })}
                    >
                      <span
                        style={css({
                          color: "#4A2E3D",
                          fontSize: "0.875rem",
                          marginTop: "0.125rem",
                        })}
                      >
                        ○
                      </span>
                      <p
                        style={css({
                          color: "#4A2E3D",
                          fontSize: "0.9rem",
                          margin: 0,
                          lineHeight: "1.5",
                          opacity: 0.85,
                          "@media (min-width: 768px)": {
                            fontSize: "0.95rem",
                          },
                        })}
                      >
                        {featuredEvent.details
                          .split(/[.!?]/)
                          .slice(0, 1)
                          .join("")}
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div
                      style={css({
                        display: "flex",
                        gap: "0.75rem",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                        "@media (max-width: 640px)": {
                          justifyContent: "flex-start",
                        },
                      })}
                    >
                      {featuredEvent.registrationOpen &&
                        featuredEvent.registrationFee === 0 && (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/event/${featuredEvent.id}#event-registration`
                            )
                          }
                          title="Register for this event"
                          style={css({
                            backgroundColor: "#FF8C42",
                            color: "white",
                            border: "none",
                            borderRadius: "9999px",
                            padding: "0.625rem 1.5rem",
                            fontSize: "0.9rem",
                            fontWeight: "700",
                            cursor: "pointer",
                            boxShadow: "0 3px 6px rgba(255, 140, 66, 0.3)",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "#FF7A2E",
                              transform: "translateY(-1px)",
                              boxShadow: "0 4px 8px rgba(255, 140, 66, 0.4)",
                            },
                            "@media (min-width: 768px)": {
                              padding: "0.75rem 2rem",
                              fontSize: "1rem",
                            },
                          })}
                        >
                          RSVP
                        </button>
                      )}
                      {featuredEvent.registrationOpen &&
                        featuredEvent.registrationFee !== 0 &&
                        featuredEvent.facebookLink?.trim() && (
                        <button
                          type="button"
                          onClick={() =>
                            window.open(featuredEvent.facebookLink, "_blank")
                          }
                          title="RSVP on Facebook"
                          style={css({
                            backgroundColor: "#FF8C42",
                            color: "white",
                            border: "none",
                            borderRadius: "9999px",
                            padding: "0.625rem 1.5rem",
                            fontSize: "0.9rem",
                            fontWeight: "700",
                            cursor: "pointer",
                            boxShadow: "0 3px 6px rgba(255, 140, 66, 0.3)",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "#FF7A2E",
                              transform: "translateY(-1px)",
                              boxShadow: "0 4px 8px rgba(255, 140, 66, 0.4)",
                            },
                            "@media (min-width: 768px)": {
                              padding: "0.75rem 2rem",
                              fontSize: "1rem",
                            },
                          })}
                        >
                          RSVP
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => navigate(`/event/${featuredEvent.id}`)}
                        title="View event details"
                        style={css({
                          backgroundColor: "#FF4B5C",
                          color: "white",
                          border: "none",
                          borderRadius: "9999px",
                          padding: "0.625rem 1.5rem",
                          fontSize: "0.9rem",
                          fontWeight: "700",
                          cursor: "pointer",
                          boxShadow: "0 3px 6px rgba(255, 75, 92, 0.3)",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#E63946",
                            transform: "translateY(-1px)",
                            boxShadow: "0 4px 8px rgba(255, 75, 92, 0.4)",
                          },
                          "@media (min-width: 768px)": {
                            padding: "0.75rem 2rem",
                            fontSize: "1rem",
                          },
                        })}
                      >
                        Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p
                style={css({
                  textAlign: "center",
                  color: "#555555",
                  fontSize: "0.95rem",
                  padding: "2rem 1rem",
                  margin: 0,
                  marginTop: "1rem",
                })}
              >
                No upcoming events at this time. Check back soon!
              </p>
            )}
          </div>
        </section>

        {/* Stories Section */}
        <section className="section-mobile">
          <div
            style={css({
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "1rem",
            })}
          >
            <h2 className="heading-mobile" style={css({ margin: 0 })}>
              Stories
            </h2>
            <span style={css({ fontSize: "1.2rem" })}>🍃</span>
          </div>
          <p
            style={css({
              fontSize: "0.9rem",
              color: tokens.colors.neutral[600],
              marginBottom: "2rem",
            })}
          >
            Core words: happy · sad · angry · calm
          </p>

          {monthlySections.map((section, sectionIndex) => {
            const monthBgColor = getMonthBackgroundColor(
              section.theme.monthNumber
            );

            return (
              <div
                key={section.theme.monthId || sectionIndex}
                style={css({ marginBottom: "3rem" })}
              >
                {/* Month Header - Card with month-specific background */}
                <div
                  style={css({
                    backgroundColor: monthBgColor,
                    borderRadius: "16px",
                    padding: "1.5rem",
                    marginBottom: "1.5rem",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: "-20px",
                      right: "-20px",
                      width: "120px",
                      height: "120px",
                      background:
                        "radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, transparent 70%)",
                      borderRadius: "50%",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: "10px",
                      right: "30px",
                      width: "80px",
                      height: "80px",
                      background:
                        "radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)",
                      borderRadius: "50%",
                    },
                  })}
                >
                  <div
                    style={css({
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      marginBottom: "0.5rem",
                      position: "relative",
                      zIndex: 1,
                    })}
                  >
                    <span style={css({ fontSize: "1.2rem" })}>🍃</span>
                    <h3
                      style={css({
                        fontSize: "1.25rem",
                        fontWeight: "700",
                        color: "#4A2E3D",
                        margin: 0,
                        "@media (min-width: 768px)": {
                          fontSize: "1.5rem",
                        },
                      })}
                    >
                      Month {section.theme.monthNumber} · {section.theme.title}
                    </h3>
                  </div>

                  {/* Core Words */}
                  {section.theme.coreWordsList &&
                    section.theme.coreWordsList.length > 0 && (
                      <p
                        style={css({
                          fontSize: "0.9rem",
                          color: tokens.colors.neutral[600],
                          margin: 0,
                          position: "relative",
                          zIndex: 1,
                        })}
                      >
                        Core words: {section.theme.coreWordsList.join(" · ")}
                      </p>
                    )}
                </div>

                {/* Story Cards Grid */}
                <div
                  className="grid-mobile"
                  style={css({
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(300px, 1fr))",
                    gap: "1.5rem",
                    "@media (min-width: 768px)": {
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(350px, 1fr))",
                    },
                  })}
                >
                  {section.stories.map((story) => {
                    // Get resource buttons - prioritize specific types
                    const amazonResource = story.resourcesList?.find(
                      (r) => r.type === ResourceType.RESOURCE_TYPE_AMAZON_BOOK
                    );
                    const youtubeResource = story.resourcesList?.find(
                      (r) =>
                        r.type === ResourceType.RESOURCE_TYPE_YOUTUBE_FULL ||
                        r.type === ResourceType.RESOURCE_TYPE_YOUTUBE_SHORTS
                    );
                    const audioResources = story.resourcesList
                      ?.filter(
                        (r) =>
                          r.type === ResourceType.RESOURCE_TYPE_AUDIO ||
                          r.type === ResourceType.RESOURCE_TYPE_YOUTUBE_MUSIC
                      )
                      .slice(0, 2);
                    const ebookResource = story.resourcesList?.find(
                      (r) => r.type === ResourceType.RESOURCE_TYPE_EBOOK
                    );

                    return (
                      <div
                        key={story.id}
                        style={css({
                          backgroundColor: "#ffffff",
                          overflow: "hidden",
                          borderRadius: "16px",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                          display: "flex",
                          flexDirection: "column",
                        })}
                      >
                        {/* Top Section - Image with Overlays */}
                        <div
                          style={css({
                            position: "relative",
                            height: "200px",
                            "@media (min-width: 768px)": { height: "240px" },
                          })}
                        >
                          <img
                            src={story.coverImageUrl || "/placeholder.jpg"}
                            alt={story.title}
                            role="button"
                            tabIndex={0}
                            className="story-card-image-clickable"
                            onClick={() => navigate(`/story/${story.id}`)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                navigate(`/story/${story.id}`);
                              }
                            }}
                            style={css({
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              cursor: "pointer",
                            })}
                          />
                          {/* Month Metadata Bar - Banner shape with pointed left end */}
                          <div
                            style={css({
                              position: "absolute",
                              bottom: "1rem",
                              left: "1rem",
                              backgroundColor: getMonthColor(story.monthNumber),
                              padding: "0.5rem 1rem 0.5rem 1.5rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              clipPath:
                                "polygon(12px 0, 100% 0, 100% 100%, 12px 100%, 0 50%)",
                              maxWidth: "calc(100% - 2rem)",
                              zIndex: 2,
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
                            })}
                          >
                            <span
                              style={css({
                                fontSize: "1rem",
                                color: "#FFEB3B",
                                flexShrink: 0,
                                lineHeight: 1,
                                filter:
                                  "drop-shadow(0 1px 1px rgba(0, 0, 0, 0.2))",
                              })}
                            >
                              ⭐
                            </span>
                            <span
                              style={css({
                                color: "white",
                                fontSize: "0.875rem",
                                fontWeight: "500",
                                whiteSpace: "nowrap",
                                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                                "@media (min-width: 768px)": {
                                  fontSize: "0.95rem",
                                },
                              })}
                            >
                              Month {story.monthNumber} • {story.monthTitle}
                            </span>
                          </div>
                        </div>

                        {/* Bottom Section - Title and Action Buttons */}
                        <div
                          style={css({
                            padding: "1rem",
                            backgroundColor: "#ffffff",
                          })}
                        >
                          {/* Repeated Title */}
                          <h4
                            style={css({
                              fontSize: "1.125rem",
                              fontWeight: "700",
                              color: "#4A2E3D",
                              margin: "0 0 1rem 0",
                              "@media (min-width: 768px)": {
                                fontSize: "1.25rem",
                              },
                            })}
                          >
                            {story.title}
                          </h4>

                          {/* Action Buttons Row */}
                          <div
                            style={css({
                              display: "flex",
                              gap: "0.5rem",
                              flexWrap: "wrap",
                            })}
                          >
                            {/* Shopping Cart Button */}
                            {amazonResource &&
                              amazonResource.available &&
                              amazonResource.url && (
                                <button
                                  type="button"
                                  className="story-card-action-btn"
                                  onClick={() =>
                                    window.open(amazonResource.url, "_blank")
                                  }
                                  style={css({
                                    backgroundColor: "#FF8C42",
                                    border: "none",
                                    borderRadius: "8px",
                                    width: "40px",
                                    height: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                  })}
                                  title="Shop this book"
                                >
                                  <span
                                    style={css({
                                      color: "#4A2E3D",
                                      fontSize: "1.125rem",
                                    })}
                                  >
                                    🛒
                                  </span>
                                </button>
                              )}

                            {/* Play Button - opens YouTube in embed modal */}
                            {youtubeResource &&
                              youtubeResource.available &&
                              youtubeResource.url && (
                                <button
                                  type="button"
                                  className="story-card-action-btn"
                                  onClick={() =>
                                    openEmbedModal(
                                      toEmbeddableUrl(youtubeResource.url),
                                      {
                                        title: `${story.title} – Video`,
                                      }
                                    )
                                  }
                                  style={css({
                                    backgroundColor: getMonthColor(
                                      story.monthNumber
                                    ),
                                    border: "none",
                                    borderRadius: "8px",
                                    width: "40px",
                                    height: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                  })}
                                  title="Watch Video"
                                >
                                  <span
                                    style={css({
                                      color: "white",
                                      fontSize: "1rem",
                                    })}
                                  >
                                    ▶
                                  </span>
                                </button>
                              )}

                            {/* Music Note Buttons - YouTube Music opens in embed modal */}
                            {audioResources &&
                              audioResources.map(
                                (resource, idx) =>
                                  resource.available &&
                                  resource.url && (
                                    <button
                                      key={idx}
                                      type="button"
                                      className="story-card-action-btn"
                                      onClick={() =>
                                        isYoutubeUrl(resource.url)
                                          ? openEmbedModal(
                                              toEmbeddableUrl(resource.url),
                                              {
                                                title: `${story.title} – Music`,
                                              }
                                            )
                                          : window.open(resource.url, "_blank")
                                      }
                                      style={css({
                                        backgroundColor: "#D4A5C7",
                                        border: "none",
                                        borderRadius: "8px",
                                        width: "40px",
                                        height: "40px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        boxShadow:
                                          "0 2px 4px rgba(0, 0, 0, 0.1)",
                                      })}
                                      title={
                                        isYoutubeUrl(resource.url)
                                          ? "Watch Music"
                                          : "Listen"
                                      }
                                    >
                                      <span
                                        style={css({
                                          color: "white",
                                          fontSize: "1.125rem",
                                          fontWeight: "bold",
                                        })}
                                      >
                                        ♪
                                      </span>
                                    </button>
                                  )
                              )}

                            {/* Book/Pages Icon Button - opens ebook in embed modal */}
                            {ebookResource &&
                              ebookResource.available &&
                              ebookResource.url && (
                                <button
                                  type="button"
                                  className="story-card-action-btn"
                                  onClick={() =>
                                    openEmbedModal(
                                      toEmbeddableUrl(ebookResource.url),
                                      {
                                        title: `${story.title} – Ebook`,
                                      }
                                    )
                                  }
                                  style={css({
                                    backgroundColor: "#90CDCE",
                                    border: "none",
                                    borderRadius: "8px",
                                    width: "40px",
                                    height: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                  })}
                                  title="Read Ebook"
                                >
                                  <span
                                    style={css({
                                      color: "white",
                                      fontSize: "1.125rem",
                                    })}
                                  >
                                    📖
                                  </span>
                                </button>
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {monthlySections.length === 0 && (
            <p
              style={css({
                textAlign: "center",
                color: tokens.colors.neutral[600],
              })}
            >
              No stories available at this time. Check back soon!
            </p>
          )}
        </section>
      </main>
      <EmbedModal />
    </>
  );
}
